import { EffectReducer } from 'use-effect-reducer'
import { getEffectForRetry } from './effects'
import { PaymentEffect, PaymentEvent, PaymentState } from './types'

// Note no retry on capture at the moment (backend needs some implementation)
const retryableStatuses: Array<PaymentState['status']> = [
  'connectingToTerminal',
  'collecting',
  'creatingIntent',
]

export const isRetryable = (status: PaymentState['status']) =>
  retryableStatuses.includes(status)

export const paymentStateReducer: EffectReducer<
  PaymentState,
  PaymentEvent,
  PaymentEffect
> = (state, event, exec) => {
  switch (event.type) {
    case 'PAIRING_OK':
      // We only expect this coming from fetchingPairing
      if (state.status !== 'fetchingPairing') return state

      exec({
        type: 'connectTerminalEffect',
        pairingSN: event.pairing.sn,
        isSimulated: event.pairing.isSimulated,
        terminalID: event.pairing.terminalID,
        terminalModel: event.pairing.terminalModel,
      })

      return {
        ...state,
        pairing: event.pairing,
        status: 'connectingToTerminal',
      } as PaymentState

    case 'TERMINAL_OK': {
      if (state.status !== 'connectingToTerminal') return state

      const { terminal, terminalMonitoringInstance } = event
      exec({ type: 'createIntentEffect', terminal })

      return {
        ...state,
        terminal,
        terminalMonitoringInstance,
        status: 'creatingIntent',
      } as PaymentState
    }

    case 'INTENT_OK': {
      if (state.status !== 'creatingIntent') return state
      const { clientSecret } = event
      const { terminal, terminalMonitoringInstance } = state

      exec({
        type: 'collectEffect',
        terminal,
        terminalMonitoringInstance,
        clientSecret,
        isRetry: false,
      })

      return {
        ...state,
        clientSecret,
        status: 'collecting',
      } as PaymentState
    }

    case 'COLLECT_OK': {
      if (state.status !== 'collecting') return state
      const { intent } = event
      exec({ type: 'captureEffect', intent })

      return {
        ...state,
        intent,
        status: 'capturing',
      } as PaymentState
    }

    case 'CAPTURE_OK':
      if (state.status !== 'capturing') return state

      return {
        ...state,
        transaction: event.transaction,
        status: 'done',
      } as PaymentState

    case 'RETRY': {
      const { previousState } = event

      const effect = getEffectForRetry(previousState)
      if (!effect) return state

      exec(effect)

      return previousState
    }

    case 'ERROR': {
      return {
        status: 'error',
        errorMessage: event.errorMessage,
        previousState: state,
        terminal: 'terminal' in state ? state.terminal : undefined,
      }
    }

    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = event
      return _exhaustiveCheck
    }
  }
}
